// Persistent eligibility panel for weight management pathways.
// Compact strip of eligible drugs; removed drugs and reasons expand on demand.

import React, { useId, useState } from "react";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import type { EliminationLogEntry } from "@/lib/pharmacyFirst/types/audit.types";

export interface PersistentEligibilityPanelProps {
  currentEligibleDrugIds: string[];
  initialEligibleDrugIds: string[];
  eliminationLog: EliminationLogEntry[];
  drugLabels: Record<string, string>;
  isDarkMode: boolean;
}

function RemovedItem({
  label,
  reason,
  isDarkMode,
}: {
  label: string;
  reason?: string;
  isDarkMode: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="flex items-start gap-2">
      <XCircle
        className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${
          isDarkMode ? "text-red-400" : "text-red-500"
        }`}
      />
      <div className="min-w-0">
        <span
          className={`text-xs line-through ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {label}
        </span>
        {reason && (
          <>
            <p
              className={`text-xs mt-0.5 ${open ? "" : "line-clamp-2"} ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {reason}
            </p>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className={`text-[11px] underline ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {open ? "Show less" : "Show more"}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export function PersistentEligibilityPanel({
  currentEligibleDrugIds,
  initialEligibleDrugIds,
  eliminationLog,
  drugLabels,
  isDarkMode,
}: PersistentEligibilityPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const eliminatedIds = initialEligibleDrugIds.filter(
    (id) => !currentEligibleDrugIds.includes(id),
  );

  const eliminationByDrugId: Record<string, EliminationLogEntry> = {};
  for (const entry of eliminationLog) {
    for (const drugId of entry.drugIds) {
      if (!eliminationByDrugId[drugId]) {
        eliminationByDrugId[drugId] = entry;
      }
    }
  }

  const hasEliminations = eliminatedIds.length > 0;
  const eligibleCount = currentEligibleDrugIds.length;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
      data-testid="eligibility-panel"
    >
      <div className="flex items-start justify-between gap-2 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          {eligibleCount === 0 ? (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                isDarkMode ? "bg-red-900/40 text-red-300" : "bg-red-50 text-red-700"
              }`}
            >
              <XCircle className="h-3 w-3" />
              No drugs eligible
            </span>
          ) : (
            <>
              <span
                className={`text-[11px] font-semibold uppercase tracking-wide ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {eligibleCount} eligible
              </span>
              {currentEligibleDrugIds.map((id) => (
                <span
                  key={id}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    isDarkMode
                      ? "bg-emerald-900/40 text-emerald-300"
                      : "bg-emerald-50 text-emerald-700"
                  }`}
                >
                  <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                  {drugLabels[id] ?? id}
                </span>
              ))}
            </>
          )}
        </div>
        {hasEliminations && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls={listId}
            className={`flex flex-shrink-0 items-center gap-1 text-xs font-medium ${
              isDarkMode ? "text-gray-300 hover:text-gray-100" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {eliminatedIds.length} removed
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {hasEliminations && expanded && (
        <ul
          id={listId}
          className={`max-h-[40vh] overflow-y-auto space-y-1.5 border-t px-3 py-2 ${
            isDarkMode ? "border-gray-700" : "border-gray-100"
          }`}
        >
          {eliminatedIds.map((id) => (
            <RemovedItem
              key={id}
              label={drugLabels[id] ?? id}
              reason={eliminationByDrugId[id]?.reason}
              isDarkMode={isDarkMode}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
